import { useEffect, useSyncExternalStore } from 'react';

import { getCombinedListSuggestions, listCombinedLists } from '../services/combinedLists';
import { getSessionKey, onSessionChange } from '../utils/session';

/**
 * A person's combined lists, for every surface that offers one (KPR-1022):
 * their saved lists (`GET /api/combined-lists`) and the matches keepr suggests
 * (`GET /api/combined-lists/suggestions`). The List's scope switch and its
 * suggestion, the collection switcher, the palette and the combined list page
 * all read this ONE session cache: both reads are rate class `expensive`, so
 * they are asked once per session, and again only when something changed
 * them (`refreshCombinedListMatches()` after a save, a rename or a delete).
 *
 *   const { lists, suggestions, loaded, error } = useCombinedListMatches();
 *
 * A sign-out or another account signing in clears it at once; an answer that
 * arrives for the account before is dropped.
 */

const EMPTY = Object.freeze({ lists: [], suggestions: [], loaded: false, loading: false, error: null, owner: null });

let state = EMPTY;
let inflight = null;
const listeners = new Set();

function emit(next) {
	state = next;
	listeners.forEach(listener => listener());
}

function subscribe(listener) {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

const getSnapshot = () => state;

onSessionChange(() => {
	inflight = null;
	emit(EMPTY);
});

function load({ force = false } = {}) {
	const owner = getSessionKey();
	if (!owner) return Promise.resolve(state);
	if (!force && state.owner === owner && (state.loaded || state.loading)) return inflight || Promise.resolve(state);
	emit({ ...state, owner, loading: true, error: null });
	const request = Promise.all([listCombinedLists(), getCombinedListSuggestions()])
		.then(([lists, suggestions]) => {
			if (getSessionKey() !== owner || inflight !== request) return state;
			emit({ lists, suggestions, loaded: true, loading: false, error: null, owner });
			return state;
		})
		.catch((error) => {
			if (getSessionKey() !== owner || inflight !== request) return state;
			// Nothing to offer is the quiet answer: the List works without it.
			emit({ ...state, loaded: true, loading: false, error, owner });
			return state;
		})
		.finally(() => { if (inflight === request) inflight = null; });
	inflight = request;
	return request;
}

/** Read both again: after a save, a rename or a delete. */
export function refreshCombinedListMatches() {
	return load({ force: true });
}

/** Put one saved list in place (a rename, an edit), without a read. */
export function rememberCombinedList(list) {
	if (!list?._id) return;
	const lists = state.lists.some(entry => entry._id === list._id)
		? state.lists.map(entry => (entry._id === list._id ? list : entry))
		: [...state.lists, list];
	emit({ ...state, lists });
}

/** Take one saved list out (a delete), without a read. */
export function forgetCombinedList(id) {
	emit({ ...state, lists: state.lists.filter(entry => entry._id !== id) });
}

/** For tests: start from nothing. */
export function resetCombinedListMatches() {
	inflight = null;
	emit(EMPTY);
}

export default function useCombinedListMatches({ enabled = true } = {}) {
	const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
	const owner = getSessionKey();
	useEffect(() => {
		if (enabled && owner) load();
	}, [enabled, owner]);
	const current = snapshot.owner === owner ? snapshot : EMPTY;
	return { lists: current.lists, suggestions: current.suggestions, loaded: current.loaded, error: current.error };
}
